/* global module: true */
import 'reflect-metadata';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { CacheProvider } from '@emotion/react';
import { BrowserRouter } from 'react-router-dom';
import Cookies from 'cookies-js';
import { Provider } from 'react-redux';
import { persistStore } from 'redux-persist';
import { REACT_APP_CONTAINER_ID } from '@common/const/reactApp';
import { IocContext } from '@cp/shared/di';
import { CONFIG_MANAGER } from 'src/shared/config';
import { createConfig } from 'src/app/config';
import { ADMIN_ROUTE_RESOLVER, AdminRouteResolver } from 'src/entities/adminRoute';
import { updateCoreApiOptions } from '@cp/shared/api/core';
import { WEBSOCKET_MANAGER, createSocketManager } from '@cp/shared/api/socket';
import { MODAL_MANAGER, MODAL_STATS_PROVIDER } from '@cp/shared/modal';
import { TOAST_TOKEN, ToastManager } from '@cp/shared/toast';
import { Container, injected } from 'brandi';
import { App } from './app';
import { configureAppStore, createReduxPersistConfig } from './redux';
import { createEmotionCache } from './createEmotionCache';
import { AppSuggestionsProvider } from './app/providers/suggestions';
import { AppDeclinationProvider } from './app/providers/declination';
import { RefModalManager } from './app/providers/modal/refManager';
import { EVENT_BUS_TOKEN, EventBus, EventBusProvider } from './app/providers/eventBus';

injected(RefModalManager, EVENT_BUS_TOKEN);
const diContainer = new Container();

diContainer.bind(CONFIG_MANAGER).toConstant(createConfig(window[Symbol.for('ENV')]));
diContainer.bind(ADMIN_ROUTE_RESOLVER).toInstance(AdminRouteResolver).inSingletonScope();
diContainer
  .bind(WEBSOCKET_MANAGER)
  .toInstance(() =>
    createSocketManager({
      CORE_SERVER_WEBSOCKET_ORIGIN: diContainer.get(CONFIG_MANAGER).getOrThrow('CORE_SERVER_WEBSOCKET_ORIGIN'),
    }),
  )
  .inSingletonScope();
diContainer.bind(EVENT_BUS_TOKEN).toInstance(EventBus).inSingletonScope();
diContainer.bind(MODAL_MANAGER).toInstance(RefModalManager).inSingletonScope();
diContainer
  .bind(MODAL_STATS_PROVIDER)
  .toInstance(() => diContainer.get(MODAL_MANAGER) as RefModalManager)
  .inTransientScope();
diContainer.bind(TOAST_TOKEN).toInstance(ToastManager).inSingletonScope();

const store = configureAppStore({
  container: diContainer,
  persistConfig: createReduxPersistConfig({ cookiesAdapter: Cookies }),
});
const container = document.getElementById(REACT_APP_CONTAINER_ID) as Element;
const root = createRoot(container);
const cache = createEmotionCache();
updateCoreApiOptions({ baseUrl: diContainer.get(CONFIG_MANAGER).getOrThrow('CORE_SERVER_ORIGIN') });

persistStore(store);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <IocContext value={diContainer}>
        <EventBusProvider>
          <AppDeclinationProvider>
            <AppSuggestionsProvider>
              <CacheProvider value={cache}>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </CacheProvider>
            </AppSuggestionsProvider>
          </AppDeclinationProvider>
        </EventBusProvider>
      </IocContext>
    </Provider>
  </React.StrictMode>,
);

if (module.hot) {
  module.hot.accept();
}
